import { useEffect, useRef, useState } from 'react';
import { LIMITS } from '@desk/contracts/constants';
import { useDesk } from './useDesk.js';
import { CallPanel } from './components/CallPanel.jsx';
import { LanguagePicker } from './components/LanguagePicker.jsx';
import { LeadForm } from './components/LeadForm.jsx';
import { MessageList } from './components/MessageList.jsx';
import { IconPhone, IconSend, IconChat, IconAlert, IconClose, IconGlobe } from './icons.jsx';

const RECORDING_NOTICE =
  'This call may be recorded and transcribed so that it can be translated and reviewed for quality.';

/**
 * Whether this widget is running inside a host page's iframe.
 *
 * The close control is only meaningful there — the launcher in `embed.js` owns
 * the frame and is the thing that actually hides it. Opened directly (which is
 * how the console operator and the UAT build see it) there is nothing to close,
 * so the button is not drawn rather than drawn and inert.
 */
const embedded = (() => {
  try {
    return window.parent !== window;
  } catch {
    return false;
  }
})();

export function Widget({ baseUrl, socketUrl, orgSlug }) {
  const desk = useDesk({ baseUrl, socketUrl, orgSlug });
  const { state } = desk;
  const [draft, setDraft] = useState('');
  const [callError, setCallError] = useState(null);
  const [langError, setLangError] = useState(null);
  const [consent, setConsent] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (state.phase === 'ready') inputRef.current?.focus();
  }, [state.phase]);

  if (state.phase === 'loading') {
    return (
      <div className="widget widget-centred">
        <div className="boot-dot" aria-hidden />
        <p className="boot-text">Connecting…</p>
      </div>
    );
  }

  if (state.phase === 'error') {
    return (
      <div className="widget widget-centred" role="alert">
        <span className="boot-ic">
          <IconAlert size={22} />
        </span>
        <p className="boot-title">We could not connect</p>
        <p className="boot-text">{state.error}</p>
        <button type="button" onClick={() => location.reload()}>
          Try again
        </button>
      </div>
    );
  }

  const config = state.config;
  const remaining = LIMITS.messageChars - draft.length;

  const submit = (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setDraft('');
    desk.send(text).catch(() => setDraft(text));
  };

  const startCall = async () => {
    setCallError(null);
    try {
      await desk.requestCall({ recordingConsent: consent });
    } catch (err) {
      setCallError(err instanceof Error ? err.message : 'Could not start a call');
    }
  };

  /* The host page owns the frame, so closing is its decision to carry out. */
  const close = () => {
    try {
      window.parent.postMessage({ type: 'desk:close' }, '*');
    } catch {
      /* A host that will not receive messages simply keeps the panel open. */
    }
  };

  // Names for the small renditions under each reply, resolved once rather than
  // per message.
  const languageNames = Object.fromEntries(config.languages.map((l) => [l.code, l.name]));

  /*
   * The visitor's own language, in that language.
   *
   * Every string the language bar and the message box show is built from this,
   * so a language added as a row on the server names itself correctly here
   * without a front-end release.
   */
  const primary = config.languages.find((l) => l.code === state.language);
  const nativeName = primary?.nativeName ?? state.language.toUpperCase();

  return (
    <div className="widget">
      <header className="widget-head">
        <span className="widget-mark" aria-hidden>
          <IconChat size={19} />
        </span>

        <div className="widget-id">
          <strong>{config.orgName}</strong>
          <span className={state.connected ? 'status online' : 'status offline'}>
            {state.connected ? 'Online now' : 'Reconnecting…'}
          </span>
        </div>

        {embedded && (
          <button type="button" className="widget-close" onClick={close} aria-label="Close chat">
            <IconClose size={16} />
          </button>
        )}
      </header>

      {state.callId ? (
        <CallPanel
          grant={state.callGrant}
          status={state.callStatus}
          queuePosition={state.queuePosition}
          subtitles={state.subtitles}
          translation={state.translation}
          translationMessage={state.translationMessage}
          socket={desk.socket}
          speechLang={desk.bcp47(state.language)}
          listenLang={desk.bcp47(state.language)}
          languageNames={languageNames}
          onHangUp={() => desk.hangUp()}
        />
      ) : (
        <>
          {langError && (
            <p className="notice danger" role="alert">
              <IconAlert size={14} />
              {langError}
            </p>
          )}

          <MessageList
            messages={state.messages}
            typing={state.agentTyping}
            languages={state.languages}
            languageNames={languageNames}
          />

          {desk.shouldPromptLead && (
            <LeadForm onSubmit={desk.submitLead} onDismiss={desk.dismissLead} />
          )}
        </>
      )}

      {!state.callId && config.voiceCallsEnabled && (
        <div className="call-offer">
          {config.recordingEnabled && (
            <label className="consent">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
              />
              {/*
                Consent is stored as a row against the call, not shown and
                forgotten. The reference system displays a notice and records
                no acknowledgement, which fails wherever both parties must
                consent to a recording.
              */}
              <span>{RECORDING_NOTICE}</span>
            </label>
          )}
          <button
            type="button"
            className="call-start"
            onClick={() => startCall()}
            disabled={!state.connected}
          >
            <IconPhone size={16} />
            Start a voice call
          </button>
          {callError && (
            <p className="notice danger" role="alert">
              <IconAlert size={14} />
              {callError}
            </p>
          )}
        </div>
      )}

      {/*
        The language row.

        It sits directly above the message box because it is a statement about
        what the visitor is about to type and what will come back — not a
        setting filed away in a header menu. The list itself is the server's,
        so adding a language is an INSERT rather than a release.
      */}
      <div className="langbar">
        <span className="langbar-label">
          <IconGlobe size={15} />
          <span>You&rsquo;re chatting in</span>
        </span>

        <LanguagePicker
          languages={config.languages}
          selected={state.languages}
          primary={state.language}
          max={LIMITS.visitorLanguages}
          disabled={!state.connected}
          onChange={(next) => {
            setLangError(null);
            desk.setLanguages(next).catch((err) =>
              setLangError(err instanceof Error ? err.message : 'Could not save that'),
            );
          }}
        />
      </div>

      <form className="composer" onSubmit={submit}>
        <div className="composer-box">
          <textarea
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, LIMITS.messageChars))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) submit(e);
            }}
            /* Named in the visitor's own language, so the box says which
               language it expects rather than leaving them to guess. */
            placeholder={`Type in ${nativeName}…`}
            rows={1}
            maxLength={LIMITS.messageChars}
            disabled={!state.connected}
          />
          <button
            type="submit"
            className="composer-send"
            disabled={!draft.trim() || state.sending || !state.connected}
            aria-label="Send"
          >
            <IconSend size={17} />
          </button>
        </div>

        {/*
          The limit is shown, not just enforced. The reference system
          advertises an 8 MB socket ceiling with no client-side limit at all,
          so an over-long message fails at the transport with no explanation.
        */}
        {remaining < 200 && (
          <div className="composer-foot">
            <span className={remaining < 40 ? 'count warn' : 'count'}>
              {remaining} characters left
            </span>
          </div>
        )}
      </form>
    </div>
  );
}
